// qualtrics.js — App -> Qualtrics handoff URL builder.
//
// The real interface (confirmed against Prolific's Qualtrics integration guide
// and Qualtrics' own query-string docs) is plain query-string passing: link
// into the survey with `?PROLIFIC_PID=...&arm=...`, and Qualtrics captures each
// param as Embedded Data via an Embedded Data element at the top of the survey
// flow. There is no API call here, only URL construction — this module has no
// mock/http split of its own because the survey *base URL* is what varies
// (backend-resolved per experiment at enroll time, see harness/enrollment-client.js
// and backend/routes/enroll.js); once a base URL exists (real or mock
// placeholder), building the link is the same pure operation either way.

/**
 * @typedef {object} QualtricsHandoffParams
 * @property {string|null} [prolificId]
 * @property {string|null} [participantId]
 * @property {string|null} [experiment]
 * @property {string|null} [arm]
 * @property {string|null} [studyId]
 * @property {string|null} [sessionId]
 */

// Maps our internal field names to the query param names Qualtrics/Prolific
// expect. PROLIFIC_PID/STUDY_ID/SESSION_ID match Prolific's own convention
// exactly (so an existing Qualtrics survey's Embedded Data setup for Prolific
// studies picks it up with zero extra configuration); participantId/experiment/arm
// are our own study metadata, added as extra Embedded Data fields.
const PARAM_NAMES = Object.freeze({
  prolificId: 'PROLIFIC_PID',
  studyId: 'STUDY_ID',
  sessionId: 'SESSION_ID',
  participantId: 'participantId',
  experiment: 'experiment',
  arm: 'arm',
});

/**
 * Pick the six identifiers a Qualtrics handoff link carries off a stored
 * participant session (the shape ParticipantGate persists via saveSession).
 * @param {import('./participant-session.js').ParticipantSession|null|undefined} session
 * @returns {QualtricsHandoffParams}
 */
export function qualtricsParamsFromSession(session) {
  return {
    prolificId: session?.prolificId ?? null,
    participantId: session?.participantId ?? null,
    experiment: session?.experiment ?? null,
    arm: session?.arm ?? null,
    studyId: session?.studyId ?? null,
    sessionId: session?.sessionId ?? null,
  };
}

/**
 * Build the participant-facing Qualtrics survey URL: the per-experiment base
 * URL from backend.study.qualtricsUrlMap plus the study identifiers as query
 * params, so Qualtrics can pick them up as Embedded Data.
 * Any query string already on `baseUrl` is preserved, except for a param name
 * that collides with one of PARAM_NAMES. The participant's actual identity
 * always wins over a same-named value in the researcher-configured base URL.
 *
 * Returns null when `baseUrl` is missing/blank (e.g. the experiment has no
 * mapped survey) so callers can render a "survey link unavailable" fallback
 * instead of a broken link.
 * @param {string|null|undefined} baseUrl
 * @param {QualtricsHandoffParams} [params]
 * @returns {string|null}
 */
export function buildQualtricsUrl(baseUrl, params = {}) {
  if (typeof baseUrl !== 'string' || !baseUrl.trim()) return null;
  let url;
  try {
    url = new URL(baseUrl);
  } catch (e) {
    console.warn('[qualtrics] baseUrl is not an absolute URL', e);
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    console.warn('[qualtrics] baseUrl must use http or https', url.protocol);
    return null;
  }
  for (const [field, paramName] of Object.entries(PARAM_NAMES)) {
    const value = params[field];
    if (value === null || value === undefined || value === '') continue;
    url.searchParams.set(paramName, String(value));
  }
  return url.toString();
}
